import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchMyBlogs } from '../store/blogSlice';
import { selectMyBlogs, selectMyBlogsLoading, selectMyBlogsError } from '../store/selectors';
import { selectUser } from '@/features/auth/store/selectors';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loading } from '@/components/ui/loading';
import { CreateBlogModal } from '../components/CreateBlogModal';
import { Plus, BookOpen, Calendar } from 'lucide-react';
import { format } from 'date-fns';

export const ProfilePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const blogs = useAppSelector(selectMyBlogs);
  const loading = useAppSelector(selectMyBlogsLoading);
  const error = useAppSelector(selectMyBlogsError);
  const user = useAppSelector(selectUser);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    dispatch(fetchMyBlogs());
  }, [dispatch]);

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="flex-1 bg-gray-50">
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 truncate">
                  Chào {user?.displayName || user?.email}!
                </h1>
                <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">
                  Quản lý các blog của bạn tại đây
                </p>
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                size="lg"
                className="w-full sm:w-auto"
              >
                <Plus className="mr-2 h-5 w-5" />
                Tạo Blog Mới
              </Button>
            </div>
          </div>

          {/* Blogs List */}
          <div>
            <h2 className="text-2xl font-bold mb-4">Danh Sách Blog</h2>

            {loading ? (
              <Loading fullScreen={false} text="Đang tải danh sách blog..." description="Vui lòng đợi trong giây lát" />
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <p className="text-red-600">{error}</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => dispatch(fetchMyBlogs())}
                >
                  Thử lại
                </Button>
              </div>
            ) : blogs.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Chưa có blog nào
                </h3>
                <p className="text-gray-600 mb-6">
                  Bắt đầu bằng cách tạo blog đầu tiên của bạn
                </p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Tạo Blog Đầu Tiên
                </Button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {blogs.map((blog) => (
                  <Card
                    key={blog.id}
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                  >
                    {blog.banner_url && (
                      <div className="h-48 overflow-hidden rounded-t-lg">
                        <img
                          src={blog.banner_url}
                          alt={blog.title}
                          className="w-full h-full object-contain bg-gray-100"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="line-clamp-2">{blog.title}</CardTitle>
                      <CardDescription className="flex items-center gap-1 text-xs">
                        <Calendar className="h-3 w-3" />
                        Tạo ngày {formatDate(blog.created_at)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>/{blog.slug}</span>
                        {String(blog.owner_id) === String(user?.id) && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            Chủ sở hữu
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Blog Modal */}
      <CreateBlogModal open={showCreateModal} onOpenChange={setShowCreateModal} />
    </div>
  );
};
